import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { localYmd as ymd } from "../lib/utils";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildMonthGrid(viewDate) {
  const first = startOfMonth(viewDate);
  // Monday-first: 0=Mon..6=Sun
  const dayOfWeek = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeek);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function MonthCalendar({
  viewDate,
  onPrev,
  onNext,
  selectedDate,
  onSelect,
  availabilityByDate, // { 'YYYY-MM-DD': {status, slot} | array }
}) {
  const cells = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="card-base p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onPrev} className="btn-ghost" aria-label="Previous month" data-testid="cal-prev-btn">
          <ChevronLeft size={18} />
        </button>
        <h3 className="font-heading font-semibold text-base sm:text-lg" data-testid="cal-month-label">{monthLabel}</h3>
        <button onClick={onNext} className="btn-ghost" aria-label="Next month" data-testid="cal-next-btn">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-widest text-ink-500 font-medium">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === viewDate.getMonth();
          const isToday = d.getTime() === today.getTime();
          const isSelected = selectedDate && key === selectedDate;
          const slots = availabilityByDate?.[key];
          const hasAvailable = Array.isArray(slots) ? slots.some((s) => s.status === "available") : slots?.status === "available";
          const hasUnavailable = Array.isArray(slots) ? slots.some((s) => s.status === "unavailable") : slots?.status === "unavailable";
          return (
            <button
              key={key}
              onClick={() => onSelect?.(key)}
              data-testid={`cal-day-${key}`}
              className={`relative aspect-square rounded-xl text-sm flex flex-col items-center justify-center transition-all
                ${inMonth ? "text-ink-800" : "text-ink-400"}
                ${isSelected ? "bg-sage-500 text-white shadow-soft" : "hover:bg-ivory-100"}
                ${isToday && !isSelected ? "ring-1 ring-sage-500/40" : ""}`}
            >
              <span className={`tabular-nums ${isSelected ? "font-semibold" : ""}`}>{d.getDate()}</span>
              <span className="absolute bottom-1.5 flex gap-0.5">
                {hasAvailable && (
                  <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-sage-500"}`} />
                )}
                {hasUnavailable && (
                  <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white/70" : "bg-terracotta-500"}`} />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
